import { FormLayout, useDownload } from "@careinfo/layout";
import { AccessControl } from "@careinfo/permission-required";
import { tw } from "@careinfo/tailwind";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import moment from "moment";
import { ClipboardEvent, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import APIS from "../../constant/EndPoint";
import { createRunsheetValidationForm } from "../../utils/validationSchemas";
import Remark from "../Remark/Remark";
import Button from "../Reusable/Button";
import Card from "../Reusable/Card";
import ComboboxInput from "../Reusable/ComboboxInput";
import DataTable from "../Reusable/DataTable";
import NACell from "../Reusable/DataTable/NACell";
import TrackingCell from "../Reusable/DataTable/TrackingCell";
import Modal from "../Reusable/Modal";
import Spinner from "../Reusable/Spinner";
import TextField from "../Reusable/TextInput";
import ReceiveCell from "../Reusable/DataTable/ReceiveCell";
import DownloadIcon from "../../Icons/DownloadIcon";

const EditReceiveManifest = (props: any) => {
  const {
    handleEdit,
    isLoading,
    data,
    handlePrint,
    isPrintLoading,
    handlecheckConsignments,
  } = props;
  const { download } = useDownload();
  const [searchParams, setSearchParams] = useSearchParams({
    limit: "10",
    page: "0",
    searchQuery: "",
  });
  const [openReviewModal, setOpenReviewModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState("");
  const selectedOrders = searchParams.get("selectedOrders");

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    setValue,
    getValues,
    clearErrors,
  } = useForm<any>({
    resolver: yupResolver(createRunsheetValidationForm),
    defaultValues: async () => {
      return {
        destination_branch: {
          name: data?.destination_branch?.name,
          id: data?.destination_branch?.id,
        },
        freight: data?.freight,
        advance: data?.advance,
        balance: data?.balance,
        note: data?.note,
      };
    },
  });
  const handleKeyDown = (event: any) => {
    if (event.key === "Enter" && event.target.value?.length > 0) {
      if (!data?.is_active) {
        handlecheckConsignments(event.target.value, setError);
      }
      event.target.value = "";
    }
  };

  const checkedAllItems = searchParams.get("selectAllItems") === "1";
  const handleIndividualSelect = (value: string, order: string) => {
    let selectedItems = searchParams.get("selectedItems")?.split(",");
    let selectedOrders = searchParams.get("selectedOrders")?.split(",");
    if (
      !selectedItems ||
      !selectedItems === undefined ||
      !selectedOrders ||
      !selectedItems === undefined
    ) {
      searchParams.set("selectedItems", value);
      searchParams.set("selectedOrders", order);
      setSearchParams(searchParams);
      return;
    }
    let itemIndex = selectedItems?.findIndex((item) => item === value);
    let orderIndex = selectedOrders?.findIndex((item) => item === order);
    if (itemIndex > -1 || orderIndex > -1) {
      selectedItems.splice(itemIndex, 1);
      selectedOrders.splice(itemIndex, 1);
      const newSelectedItems = selectedItems
        ?.map((item) => item)
        .join(",")
        .toString();
      const newSelectedOrders = selectedOrders
        ?.map((item) => item)
        .join(",")
        .toString();
      if (newSelectedItems.length && newSelectedOrders.length) {
        searchParams.set("selectedItems", newSelectedItems);
        searchParams.set("selectedOrders", newSelectedOrders);
      } else {
        searchParams.delete("selectedItems");
        searchParams.delete("selectedOrders");
      }
      searchParams.set("selectAllItems", "0");
      setSearchParams(searchParams);
    } else {
      selectedItems?.push(value);
      selectedOrders?.push(order);
      const newSelectedItems = selectedItems
        ?.map((item) => item)
        .join(",")
        .toString();
      const newSelectedOrders = selectedOrders
        ?.map((item) => item)
        .join(",")
        .toString();
      searchParams.set("selectedItems", newSelectedItems);
      searchParams.set("selectedOrders", newSelectedOrders);
      setSearchParams(searchParams);
    }
  };

  const isSelected = (value: string, order: string) => {
    const selectedItems = searchParams.get("selectedItems")?.split(",");
    const selectedOrders = searchParams.get("selectedOrders")?.split(",");
    if (!selectedItems && !selectedOrders) {
      return false;
    }
    return selectedItems.includes(value), selectedOrders.includes(order);
  };

  const handleSelectAll = (table: any) => {
    const {
      options: { data: tableData },
    } = table;
    const isSelected = searchParams.get("selectAllItems");
    if (isSelected === "1") {
      searchParams.set("selectAllItems", "0");
      searchParams.delete("selectedItems");
      searchParams.delete("selectedOrders");
      setSearchParams(searchParams);
    } else {
      const selectedItems = tableData
        ?.map((item: any) => item.id)
        .join(",")
        .toString();
      const selectedOrders = tableData
        ?.map((item: any) => item?.order?.id ?? item.id)
        .join(",")
        .toString();
      searchParams.set("selectAllItems", "1");
      searchParams.set("selectedItems", selectedItems);
      searchParams.set("selectedOrders", selectedOrders);
      setSearchParams(searchParams);
    }
  };
  const orderDefaultColumns: any = [
    {
      id: "select",
      header: ({ table }) => {
        return (
          <input
            type="checkbox"
            checked={checkedAllItems}
            onChange={() => handleSelectAll(table)}
            className={tw`cursor-pointer`}
          />
        );
      },
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={isSelected(row?.original?.id, row?.original?.order?.id)}
          onChange={() =>
            handleIndividualSelect(row?.original?.id, row?.original?.order?.id)
          }
          className={tw`cursor-pointer`}
        />
      ),
    },
    {
      header: "Date",
      accessorKey: "order.created",
      cell: ({ getValue }: any) => {
        return (
          <>
            {getValue() ? (
              <p>{moment(getValue()).format("YY-MM-DD HH:mm:ss")}</p>
            ) : (
              "N/A"
            )}
          </>
        );
      },
    },
    {
      header: "Waybill",
      accessorKey: "order.tracking_number",
      cell: TrackingCell,
    },
    {
      header: "Origin",
      accessorKey: "order.pickup_branch.name",
      cell: NACell,
    },
    {
      header: "Sender",
      accessorKey: "order.sender.full_name",
      cell: NACell,
    },
    {
      header: "Destination",
      accessorKey: "order.destination.name",
      cell: NACell,
    },

    {
      header: "Receiver",
      accessorKey: "order.receiver.full_name",
      cell: NACell,
    },

    {
      header: "Pcs",
      accessorKey: "order.piece",
      cell: NACell,
    },

    {
      header: "Remarks",
      accessorKey: "order.remarks",
      cell: ({ row, getValue }: any) => (
        <button
          onClick={() => {
            setSelectedOrder(row?.original?.order.id);
            setOpenReviewModal(true);
          }}
        >
          {getValue()} Remarks
        </button>
      ),
    },
    {
      header: "Received",
      accessorKey: "is_received",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            {getValue() ? (
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18457 2.99721 7.13633 4.39828 5.49707C5.79935 3.85782 7.69279 2.71538 9.79619 2.24015C11.8996 1.76491 14.1003 1.98234 16.07 2.86M22 4L12 14.01L9.00001 11.01"
                  stroke="#26C943"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            ) : (
              <ReceiveCell row={row} handleReceive={handleOrderReceive} />
            )}
          </>
        );
      },
    },
  ];

  const handleDownloadExcel = async () => {
    try {
      await download(
        `logisticservice/api/order/download-excel/`,
        `manifest-order-${moment().format("MMMM-Do-YYYY-h-mm-ss")}.xlsx`,
        selectedOrders
      );
    } catch (e) {
      console.error(e);
    }
  };

  const preventCopyPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    toast.error("Copy and paste is not allowed here");
  };

  const handleOrderDetailNavigate = (row: any) => {
    console.info();
  };

  const handleOrderReceive = (trackingNumber) => {
    handlecheckConsignments(trackingNumber, setError);
  };

  return (
    <>
      <AccessControl
        children={
          <FormLayout
            handleSubmit={handleSubmit}
            handleFormSubmit={handleEdit}
            setError={setError}
            buttonLabel={"Update"}
            isLoading={isLoading}
            disabled={!data?.is_active}
          >
            <div className={tw`grid  md:grid-cols-2 gap-x-4 mx-4`}>
              <ComboboxInput
                url={APIS.BRANCH}
                queryKey={"branch"}
                register={register("destination_branch")}
                errors={errors?.destination_branch}
                name={"destination_branch"}
                label={"Branch"}
                setValue={setValue}
                placeholder={"Search branch"}
                getValues={getValues}
                clearErrors={clearErrors}
                disabled={!data?.is_active}
              />
              <div className={tw`mx-4`}>
                <TextField
                  label={"Note"}
                  register={register(`note`, { disabled: !data?.is_active })}
                  errors={errors?.note}
                />
              </div>
            </div>
          </FormLayout>
        }
        allowedPermissions={["can_editupdate_manifest"]}
      />
      <Card title="Barcode Scanner">
        <>
          <div className={tw`grid grid-cols-1 w-full`}>
            <input
              type="text"
              {...register("tracking_number")}
              className={tw`w-full focus:outline-none bg-[#EEF5F4] p-2 mt-1`}
              onPaste={preventCopyPaste}
              onKeyDown={handleKeyDown}
            />
            {errors?.tracking_number && (
              <p className={tw`text-red-500 text-xs my-1`}>
                {errors?.tracking_number?.message}
              </p>
            )}
          </div>
        </>
      </Card>

      <DataTable
        url={`${APIS.MANIFEST_ITEMS}`}
        queryKey={"manifest-items"}
        columns={orderDefaultColumns}
        params={{ manifest: data?.id, order_by: "-modified" }}
        tableName={"manifest-item"}
        handleDetailNavigate={handleOrderDetailNavigate}
        actionMenu={
          <div className={tw`w-full text-right flex items-center gap-x-2`}>
            <button
              className={tw`flex gap-x-1 cursor-pointer mr-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1`}
              onClick={handleDownloadExcel}
            >
              <DownloadIcon color={"var(--primary)"} />
              Download
            </button>
            <button
              className={tw`p-1 px-5 rounded-sm border border-primary mr-2`}
              disabled={isPrintLoading}
              onClick={() => handlePrint()}
            >
              {isPrintLoading ? <Spinner /> : "Print"}
            </button>
          </div>
        }
      />
      <Modal
        isOpen={openReviewModal}
        setIsOpen={setOpenReviewModal}
        title={"Remark"}
        children={
          <>
            <div className={tw`min-w-[600px]`}>
              <Remark selectedOrder={selectedOrder} />
            </div>
          </>
        }
      />
    </>
  );
};

export default EditReceiveManifest;
